import React, { useContext } from 'react'
import { EventAgendaContext } from '@/component/Context/EventAgendaContext'
import { arrayOf, number, shape, string, oneOf, object, bool } from 'prop-types'
import styles from './SpeakerCard.module.scss'
import ResponsiveImage from '@/component/Primitive/ResponsiveImage'
import cx from 'classnames'
import Stack from '@/component/Primitive/Stack'
import Hide from '@/component/Primitive/Hide'
import SmartLink from '@/component/Primitive/SmartLink'
import Type from '@/component/Primitive/Type'
import { Highlight } from '@/component/Primitive/Events'
import Icon from '@/component/Primitive/Icon'

const SpeakerCard = ({
  title,
  subtitle,
  companyName,
  roles,
  nextAgendaItem,
  nextAgendaItems,
  image,
  theme,
  ctaLink,
  inverse,
  logo,
  fullUrlPath,
  align,
  openInNewTab,
  timezone,
  registerButtonClassName,
  registerAction,
  timezoneAbbreviation
}) => {
  const { mobile: mobileImage, desktop: desktopImage } = image || {}
  const { mobile: mobileLogo, desktop: deskotpLogo } = logo || {}

  const { handleOpenModal, setEventAgendaModalItem } = useContext(
    EventAgendaContext
  )

  const handleClick = (item) => {
    setEventAgendaModalItem({
      ...item,
      location: item.location.name,
      sessionType: item.sessionType.name,
      timezone,
      registerButtonClassName,
      registerAction,
      time: `${item.localeSafeStartTime} - ${item.localeSafeEndTime} (${timezoneAbbreviation})`
    })
    handleOpenModal()
  }

  const Component = (
    <div className={cx(styles.SpeakerCardInner, inverse && styles.inverse)}>
      <div className={styles.Content}>
        <Stack gap="small">
          <div className={styles.ImagesWrapper}>
            {desktopImage && (
              <>
                <Image device="desktop" image={desktopImage} />
                <Logo device="desktop" logo={deskotpLogo} />
              </>
            )}
            {mobileImage && (
              <>
                <Image device="mobile" image={mobileImage} />
                <Logo device="mobile" logo={mobileLogo} />
              </>
            )}
          </div>
          {roles && (
            <div className={styles.Roles}>
              {roles.map((role) => {
                return (
                  <Type
                    as="p"
                    size="body4"
                    weight="regular"
                    className={styles.Role}
                    key={role}
                  >
                    {role}
                  </Type>
                )
              })}
            </div>
          )}
          <div className={styles.TextContainer}>
            {title && (
              <Type
                as="h4"
                size={['heading9', 'heading7']}
                weight="medium"
                className={styles.Title}
              >
                <Highlight>{title}</Highlight>
              </Type>
            )}
            <div>
              {subtitle && (
                <Type
                  as="h2"
                  size="body4"
                  weight="regular"
                  className={styles.Subtitle}
                >
                  {subtitle}
                </Type>
              )}
              {companyName && (
                <Type
                  as="h3"
                  size="body4"
                  weight="regular"
                  className={styles.CompanyName}
                >
                  {companyName}
                </Type>
              )}
            </div>
          </div>
          {nextAgendaItems &&
            nextAgendaItems.map((item, index) => (
              <Type
                key={index}
                as="div"
                size="body4"
                className={styles.NextAgendaItem}
              >
                <Icon
                  width={20}
                  height={20}
                  className={styles.Calendar}
                  type="calendar"
                />{' '}
                <button
                  onClick={() => handleClick(item)}
                  className={styles.NextAgendaItemName}
                >
                  {item.name}
                </button>
              </Type>
            ))}
        </Stack>
      </div>
    </div>
  )
  const getUrl = () => {
    if (!fullUrlPath) return null
    if (fullUrlPath.includes('http:') || fullUrlPath.includes('https:'))
      return fullUrlPath
    return `/${fullUrlPath}`
  }

  return (
    <div
      className={cx(
        styles.SpeakerCard,
        theme === 'dark' && styles.dark,
        theme === 'grey' && styles.grey,
        align === 'center' && styles.center,
        (fullUrlPath || ctaLink) && styles.link
      )}
    >
      {ctaLink || getUrl() ? (
        <SmartLink
          href={ctaLink || getUrl()}
          className={styles.SpeakerCardOverlayLink}
          {...(openInNewTab && { target: '_blank' })}
        >
          {Component}
        </SmartLink>
      ) : (
        Component
      )}
    </div>
  )
}

SpeakerCard.propTypes = {
  title: string,
  subtitle: string,
  companyName: string,
  roles: arrayOf(string),
  openInNewTab: bool,
  nextAgendaItem: shape({
    name: string
  }),
  nextAgendaItems: arrayOf(
    shape({
      name: string
    })
  ),
  timezoneAbbreviation: string,
  theme: string,
  ctaLink: string,
  fullUrlPath: string,
  inverse: bool,
  timezone: string,
  registerButtonClassName: string,
  registerAction: object,
  align: oneOf(['center', 'left']),
  image: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  }),
  logo: shape({
    mobile: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    }),
    desktop: shape({
      src: string,
      srcSet: arrayOf(shape({ width: number, src: string })),
      sizes: arrayOf(string)
    })
  })
}

const Image = ({ device, image, logo, options = {} }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  const className =
    device === 'mobile' ? styles.mobileImage : styles.desktopImage
  return (
    <Hide {...hideProps} className={cx(styles.ImageWrapper)}>
      <ResponsiveImage className={cx(styles.Image, className)} {...image} />
      {logo && (
        <ResponsiveImage className={cx(styles.Logo, className)} {...logo} />
      )}
    </Hide>
  )
}

const Logo = ({ device, logo }) => {
  const hideProps =
    device === 'mobile' ? { at: 'desktop' } : { below: 'desktop' }
  return (
    <Hide className={cx(styles.LogoWrapper)} {...hideProps}>
      <ResponsiveImage className={styles.Logo} {...logo} />
    </Hide>
  )
}

Logo.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  logo: object
}

Image.propTypes = {
  device: oneOf(['mobile', 'desktop']),
  image: object,
  logo: object,
  options: object
}

export default SpeakerCard

/*
  - name
  - jobTitle
  - companyName
  - roles
  - nextAgendaItem
*/
